/**
 * Venner · Scratch card.
 *
 * Email capture with a scratch-and-win mechanic. Klaviyo has no scratch card of its
 * own — Spin to Win is its only native gamified form — so this is built in the theme.
 *
 * How it talks to Klaviyo:
 *
 *   · The email is subscribed with POST /client/subscriptions, the public Client API
 *     endpoint: it authenticates with the PUBLIC key (company_id) and answers 202.
 *     Never a private key — any visitor can read this file.
 *   · The birthday is stored with POST /client/profiles, which updates properties
 *     without touching consent that was already given.
 *   · When the Klaviyo embed is present we also `identify`, so the browser session is
 *     tied to the profile and abandoned-cart attribution is not lost.
 *
 * The prize is drawn on the client. Anyone with the console open can read the odds and
 * force the good one: the real defence is the per-code usage limits in Shopify, not the
 * secrecy of the draw.
 */

(() => {
  const STORAGE = 'venner:scratch:v1';

  // Paths where we never interrupt: they are already buying or signing in.
  const BLOCKED_PATHS = /^\/(checkouts?|cart\/|account|tools|apps|challenge|password|\d+\/(checkouts|orders))/;

  const BOT =
    /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|whatsapp|telegram|preview|lighthouse|headless|pingdom|gtmetrix|semrush|ahrefs/i;

  const KLAVIYO_HOST = 'https://a.klaviyo.com';

  // How long we wait for the country notice before giving up on it, and the breath we
  // leave its sheet to slide off screen before the scratch card opens.
  const GEO_WAIT = 8000;
  const GEO_GRACE = 600;

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function write(value) {
    try {
      localStorage.setItem(STORAGE, JSON.stringify(value));
    } catch {
      /* Private mode or blocked storage: the popup still works, it just stops
         remembering that it was already shown. */
    }
  }

  const days = (n) => n * 24 * 60 * 60 * 1000;

  function fill(template, values) {
    return String(template || '').replace(/\{(PRIZE|CODE)\}/g, (match, key) => {
      return key === 'PRIZE' ? values.prize : values.code;
    });
  }

  /**
   * Weighted draw. Weights need not add up to 100: they are shared in proportion,
   * so adding or removing a prize does not force recalculating the others.
   */
  function pickPrize(prizes) {
    const total = prizes.reduce((sum, prize) => sum + Math.max(0, prize.weight || 0), 0);
    if (total <= 0) return prizes[prizes.length - 1];

    let ticket = Math.random() * total;
    for (const prize of prizes) {
      ticket -= Math.max(0, prize.weight || 0);
      if (ticket <= 0) return prize;
    }
    return prizes[prizes.length - 1];
  }

  /**
   * Measurement. The same event goes out through three doors because each one
   * reaches somewhere different, and no single one is enough:
   *
   *   · `Shopify.analytics.publish` — the official bridge to the Web Pixels
   *     sandbox. It is the only route to an analytics tool that lives in that
   *     sandbox, which is where Shopify puts GA4; a bare `gtag` on the page never
   *     reaches it (measured on the Mexican store: the event went to Google Ads
   *     and never to GA4).
   *   · `dataLayer` + `gtag` — what page-level Google tags listen to. This store
   *     has no Google tag today, so these are no-ops until one is installed.
   *   · Klaviyo — the signup only, and only after `identify`: its onsite event API
   *     drops anyone without a profile yet, so an anonymous view cannot be counted
   *     there.
   *
   * None of them may take the popup down: all three are wrapped.
   */
  function report(eventName, payload) {
    try {
      // Shopify asks for a prefix on custom events so they do not collide with
      // its own or another app's. The unprefixed name stays for dataLayer and
      // gtag, which is what page-level Google tags listen to.
      window.Shopify?.analytics?.publish(`venner:${eventName}`, payload);
    } catch {
      /* With no web pixels active, nothing happens. */
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...payload });
    } catch {
      /* No dataLayer, no problem: the popup does not depend on measurement. */
    }

    try {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, payload);
    } catch {
      /* Same: never break the flow over an analytics error. */
    }
  }

  /** Appends the coupon to the destination URL using Shopify's native route. */
  function discountUrl(code, destination) {
    const target = destination && destination !== '#' ? destination : '/collections/all';
    let path = target;

    try {
      // Theme editor URLs can arrive absolute; we keep just the path.
      const parsed = new URL(target, window.location.origin);
      path = parsed.pathname + parsed.search + parsed.hash;
    } catch {
      /* Relative path, used as-is. */
    }

    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    return `${root}/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(path)}`;
  }

  class VennerScratch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      const configNode = this.querySelector('[data-venner-scratch-config]');
      if (!configNode) return;

      try {
        this.config = JSON.parse(configNode.textContent);
      } catch {
        return;
      }

      if (!this.config.prizes?.length || !this.config.companyId || !this.config.listId) return;

      this.dialog = this.querySelector('.venner-scratch__backdrop');
      this.ticket = this.querySelector('[data-vs-ticket]');
      this.canvas = this.querySelector('[data-vs-canvas]');
      this.emailForm = this.querySelector('[data-vs-step="email"]');
      this.birthdayForm = this.querySelector('[data-vs-birthday]');
      this.steps = Array.from(this.querySelectorAll('[data-vs-step]'));

      this.designMode = Boolean(window.Shopify?.designMode);
      this.scratchedOnce = false;

      this.bind();
      this.preloadCover();

      if (this.designMode) {
        // In the editor the popup is governed by section selection, not by the rules.
        document.addEventListener('shopify:section:select', (event) => {
          if (event.target.contains(this)) this.open();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.target.contains(this)) this.close();
        });
        return;
      }

      if (this.shouldSkip()) return;
      this.watchTriggers();
    }

    /* ------------------------------------------------------------ display rules */

    shouldSkip() {
      if (BOT.test(navigator.userAgent)) return true;
      if (BLOCKED_PATHS.test(window.location.pathname)) return true;

      const saved = read();
      if (!saved) return false;

      // Anyone who already signed up never sees it again.
      if (saved.s === 'won') return true;

      const wait = days(this.config.frequencyDays || 0);
      return wait > 0 && Date.now() - (saved.ts || 0) < wait;
    }

    watchTriggers() {
      const fire = () => this.open();

      if (this.config.delay >= 0) {
        this.timer = window.setTimeout(fire, (this.config.delay || 0) * 1000);
      }

      if (this.config.scrollPercent > 0) {
        this.onScroll = () => {
          const scrollable = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollable <= 0) return;
          const reached = (window.scrollY / scrollable) * 100 >= this.config.scrollPercent;
          if (reached) fire();
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      // Exit intent only exists with a pointer: on mobile there is no leaving upwards.
      if (this.config.exitIntent && window.matchMedia('(hover: hover)').matches) {
        this.onLeave = (event) => {
          if (event.clientY <= 0) fire();
        };
        document.addEventListener('mouseout', this.onLeave);
      }
    }

    stopTriggers() {
      window.clearTimeout(this.timer);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
    }

    /* ------------------------------------------------- living with the country notice */

    /**
     * The country notice (`sections/venner-geo`) sets `data-state="done"` once it has
     * decided: either it did not apply, or the visitor dismissed it. If the section is not
     * on the page there is nothing to wait for.
     */
    geoPending() {
      if (this.geoWaived) return false;
      const geo = document.querySelector('venner-geo');
      return Boolean(geo) && geo.dataset.state !== 'done';
    }

    deferToGeo() {
      if (this.geoWaiting) return;
      this.geoWaiting = true;

      // The triggers already did their job: from here on the country notice is in charge.
      this.stopTriggers();

      const resume = () => {
        window.clearTimeout(this.geoTimer);
        document.removeEventListener('venner:geo:settled', resume);
        this.geoWaiting = false;
        // We waited once already: no need to wait again on the retry.
        this.geoWaived = true;
        window.setTimeout(() => this.open(), GEO_GRACE);
      };

      document.addEventListener('venner:geo:settled', resume);

      // Safety net in case the notice never settles (its country fetch failed). If the
      // notice is actually on screen we keep waiting: dismissing it always emits the signal.
      this.geoTimer = window.setTimeout(() => {
        if (!document.querySelector('venner-geo[open]')) resume();
      }, GEO_WAIT);
    }

    /* ------------------------------------------------------------ open and close */

    open() {
      if (this.hasAttribute('open')) return;

      // The country notice goes first: it decides which store the visitor belongs to, and
      // the scratch card on top of it covered the question right when it had to be answered.
      if (this.geoPending()) {
        this.deferToGeo();
        return;
      }

      this.stopTriggers();

      this.prize = pickPrize(this.config.prizes);
      this.querySelector('[data-vs-prize]').textContent = this.prize.label;

      this.showStep('scratch');
      this.setAttribute('open', '');

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      this.paintCover();

      report('scratch_view', { prize: this.prize.label, coupon: this.prize.code });
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');

      if (this.dialog.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }

      if (this.designMode) return;

      // If they already won, the 'won' state is written and must not be downgraded.
      const saved = read();
      if (saved?.s !== 'won') {
        write({ s: 'closed', ts: Date.now() });
        report('scratch_abandoned', {
          prize: this.prize?.label,
          scratched: this.ticket.hasAttribute('data-revealed')
        });
      }
    }

    showStep(name) {
      for (const step of this.steps) {
        step.hidden = step.dataset.vsStep !== name;
      }
    }

    bind() {
      for (const button of this.querySelectorAll('[data-vs-close]')) {
        button.addEventListener('click', () => this.close());
      }

      // <dialog> already closes on Escape; this keeps our own state in sync.
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      const reveal = this.querySelector('[data-vs-reveal]');
      if (reveal) reveal.addEventListener('click', () => this.reveal());

      this.bindCanvas();

      this.emailForm.addEventListener('submit', (event) => {
        event.preventDefault();
        this.subscribe();
      });

      for (const copy of this.querySelectorAll('[data-vs-copy]')) {
        copy.addEventListener('click', () => this.copyCode(copy));
      }

      if (this.birthdayForm) {
        this.birthdayForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.saveBirthday();
        });
      }

      // If the width changes before scratching we must repaint the cover: the canvas
      // is resized and the old content would end up stretched.
      window.addEventListener('resize', () => {
        if (!this.hasAttribute('open') || this.scratchedOnce) return;
        if (this.ticket.hasAttribute('data-revealed')) return;
        window.clearTimeout(this.repaintTimer);
        this.repaintTimer = window.setTimeout(() => this.paintCover(), 150);
      });
    }

    /* ------------------------------------------------------------ scratchable cover */

    /**
     * The cover image is requested on page load, not when the popup opens.
     *
     * Measured on slow 4G against the Mexican store: asking for it on open left the
     * ticket grey for 4.6s. It is not weight — 61 kB as WebP, which Shopify negotiates
     * on its own — it is that the request joined the queue behind the rest of the page
     * at exactly that moment.
     *
     * It goes in the browser's idle gap so it does not get in the way of what the
     * visitor is actually looking at, with a cap in case that gap never comes.
     */
    preloadCover() {
      if (!this.config.coverImage) return;

      const start = () => {
        this.coverImg = new Image();
        this.coverImg.src = this.config.coverImage;
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(start, { timeout: 3000 });
      } else {
        window.setTimeout(start, 1200);
      }
    }

    paintCover() {
      const canvas = this.canvas;
      const rect = this.ticket.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(rect.width * ratio);
      canvas.height = Math.round(rect.height * ratio);

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      this.ctx = ctx;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

      // The colour always goes first: the ticket must never look transparent,
      // not even while the image is loading.
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = getComputedStyle(this).getPropertyValue('--vs-cover').trim() || '#c9c9c9';
      ctx.fillRect(0, 0, rect.width, rect.height);

      this.paintCoverFinish(rect);

      if (this.config.coverImage) {
        const image = this.coverImg || new Image();

        const draw = () => {
          if (this.ticket.hasAttribute('data-revealed') || this.scratchedOnce) return;
          const scale = Math.max(rect.width / image.width, rect.height / image.height);
          const width = image.width * scale;
          const height = image.height * scale;
          ctx.drawImage(image, (rect.width - width) / 2, (rect.height - height) / 2, width, height);
          this.paintCoverFinish(rect);
        };

        // If the preload already finished, it draws in the same frame and nobody sees grey.
        if (image.complete && image.naturalWidth) {
          draw();
        } else {
          image.onload = draw;
          if (!image.src) image.src = this.config.coverImage;
        }
      }
    }

    /**
     * The veil and the text, in that order. They travel together on purpose: the
     * veil only exists so the text reads on top of the photo, and the image is
     * drawn asynchronously, so splitting them ends up painting the text beneath.
     */
    paintCoverFinish(rect) {
      const strength = (this.config.coverTintStrength || 0) / 100;
      const tint = this.config.coverTint;

      if (strength > 0 && (tint === 'dark' || tint === 'light')) {
        const ctx = this.ctx;
        ctx.save();
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = tint === 'dark' ? `rgb(0 0 0 / ${strength})` : `rgb(255 255 255 / ${strength})`;
        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.restore();
      }

      this.paintCoverText(rect);
    }

    paintCoverText(rect) {
      const text = this.config.coverText;
      if (!text) return;

      const ctx = this.ctx;
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = getComputedStyle(this).getPropertyValue('--vs-cover-text').trim() || '#6b6b6b';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if ('letterSpacing' in ctx) ctx.letterSpacing = '3px';
      ctx.font = `600 ${Math.max(13, Math.round(rect.width * 0.055))}px ${
        getComputedStyle(this).fontFamily || 'sans-serif'
      }`;
      ctx.fillText(text, rect.width / 2, rect.height / 2);
      ctx.restore();
    }

    bindCanvas() {
      const canvas = this.canvas;
      let drawing = false;
      let last = null;
      let checkedAt = 0;

      const pointAt = (event) => {
        const rect = canvas.getBoundingClientRect();
        return { x: event.clientX - rect.left, y: event.clientY - rect.top };
      };

      const scratch = (from, to) => {
        const ctx = this.ctx;
        if (!ctx) return;

        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineWidth = this.config.brushSize || 34;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();

        // A lone point (a tap without dragging) leaves no trace with lineTo.
        ctx.beginPath();
        ctx.arc(to.x, to.y, (this.config.brushSize || 34) / 2, 0, Math.PI * 2);
        ctx.fill();
      };

      canvas.addEventListener('pointerdown', (event) => {
        if (this.ticket.hasAttribute('data-revealed')) return;
        drawing = true;
        this.scratchedOnce = true;

        // Capturing the pointer keeps the stroke if the finger leaves the ticket.
        // Not every browser allows it for every pointer, and failing here must not
        // stop someone from scratching.
        try {
          canvas.setPointerCapture(event.pointerId);
        } catch {
          /* We carry on without capture: `pointerleave` closes the stroke. */
        }

        last = pointAt(event);
        scratch(last, last);
      });

      canvas.addEventListener('pointermove', (event) => {
        if (!drawing) return;
        event.preventDefault();
        const next = pointAt(event);
        scratch(last || next, next);
        last = next;

        // Reading the pixels is expensive: once every 150 ms is enough.
        const now = Date.now();
        if (now - checkedAt > 150) {
          checkedAt = now;
          this.checkProgress();
        }
      });

      const stop = () => {
        if (!drawing) return;
        drawing = false;
        last = null;
        this.checkProgress();
      };

      canvas.addEventListener('pointerup', stop);
      canvas.addEventListener('pointercancel', stop);
      canvas.addEventListener('pointerleave', stop);
    }

    /** Share of the cover already scratched, sampling the grid in 8 px steps. */
    checkProgress() {
      const ctx = this.ctx;
      if (!ctx || this.ticket.hasAttribute('data-revealed')) return;

      const { width, height } = this.canvas;
      const pixels = ctx.getImageData(0, 0, width, height).data;
      const stride = 8;

      let clear = 0;
      let total = 0;

      for (let y = 0; y < height; y += stride) {
        for (let x = 0; x < width; x += stride) {
          total += 1;
          if (pixels[(y * width + x) * 4 + 3] < 32) clear += 1;
        }
      }

      if (total && (clear / total) * 100 >= (this.config.threshold || 55)) this.reveal();
    }

    reveal() {
      if (this.ticket.hasAttribute('data-revealed')) return;
      this.ticket.setAttribute('data-revealed', '');

      const hint = this.querySelector('[data-vs-hint]');
      if (hint) hint.hidden = true;

      report('scratch_scratched', { prize: this.prize.label, coupon: this.prize.code });

      const motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.setTimeout(() => this.toEmailStep(), motion ? 0 : 500);
    }

    toEmailStep() {
      const won = this.querySelector('[data-vs-won]');
      if (won) won.textContent = this.prize.label;

      const values = { prize: this.prize.label, code: this.prize.code };
      for (const node of this.querySelectorAll('[data-vs-step2-text]')) {
        node.innerHTML = fill(node.dataset.vsTemplate || (node.dataset.vsTemplate = node.innerHTML), values);
      }

      this.showStep('email');

      // No autofocus on mobile: popping the keyboard open hides the prize.
      if (window.matchMedia('(hover: hover)').matches) {
        this.querySelector('[data-vs-email]')?.focus();
      }
    }

    /* ------------------------------------------------------------ Klaviyo */

    klaviyoUrl(path) {
      return `${KLAVIYO_HOST}${path}?company_id=${encodeURIComponent(this.config.companyId)}`;
    }

    klaviyoHeaders() {
      return {
        'content-type': 'application/vnd.api+json',
        revision: this.config.revision
      };
    }

    async subscribe() {
      const input = this.querySelector('[data-vs-email]');
      const error = this.querySelector('[data-vs-error]');
      const button = this.querySelector('[data-vs-submit]');
      const label = this.querySelector('[data-vs-submit-label]');
      const email = input.value.trim();

      const showError = (message) => {
        error.textContent = message;
        error.hidden = false;
        input.setAttribute('aria-invalid', 'true');
      };

      if (!input.checkValidity() || !email) {
        showError(this.config.errorEmail);
        input.focus();
        return;
      }

      error.hidden = true;
      input.removeAttribute('aria-invalid');
      button.disabled = true;
      const original = label.textContent;
      if (this.config.sendingText) label.textContent = this.config.sendingText;

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.config.source,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  properties: {
                    scratch_prize: this.prize.label,
                    scratch_coupon: this.prize.code,
                    signup_source: this.config.source
                  },
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: this.config.listId } }
          }
        }
      };

      try {
        const response = await fetch(this.klaviyoUrl('/client/subscriptions'), {
          method: 'POST',
          headers: this.klaviyoHeaders(),
          body: JSON.stringify(payload)
        });

        if (!response.ok) throw new Error(`Klaviyo answered ${response.status}`);

        this.email = email;
        write({ s: 'won', code: this.prize.code, label: this.prize.label, ts: Date.now() });

        // Ties the browser session to the profile so attribution is not lost.
        // `track` goes after `identify` on purpose: Klaviyo drops events from
        // anyone who does not have a profile yet.
        try {
          window.klaviyo?.push(['identify', { $email: email }]);
          window.klaviyo?.push([
            'track',
            'Scratch card signup',
            { Prize: this.prize.label, Coupon: this.prize.code, Source: this.config.source }
          ]);
        } catch {
          /* The Klaviyo embed may not have loaded yet. */
        }

        report('scratch_signup', { prize: this.prize.label, coupon: this.prize.code });

        this.toCodeStep();
      } catch {
        showError(this.config.errorNetwork);
        button.disabled = false;
        label.textContent = original;
      }
    }

    toCodeStep() {
      const values = { prize: this.prize.label, code: this.prize.code };

      for (const node of this.querySelectorAll('[data-vs-success-text]')) {
        node.innerHTML = fill(node.dataset.vsTemplate || (node.dataset.vsTemplate = node.innerHTML), values);
      }

      this.querySelector('[data-vs-code]').textContent = this.prize.code;

      const note = this.querySelector('[data-vs-note]');
      if (note && this.prize.note) {
        note.textContent = this.prize.note;
        note.hidden = false;
      }

      const href = discountUrl(this.prize.code, this.prize.applyUrl);
      for (const link of this.querySelectorAll('[data-vs-apply]')) {
        link.href = href;
      }

      this.showStep('code');
    }

    async copyCode(button) {
      const hint = button.querySelector('[data-vs-copy-hint]');
      try {
        await navigator.clipboard.writeText(this.prize.code);
      } catch {
        // Safari without permission: selecting the text still allows copying by hand.
        const range = document.createRange();
        range.selectNodeContents(button.querySelector('[data-vs-code]'));
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }

      if (!hint || !this.config.copiedText) return;
      const original = hint.textContent;
      hint.textContent = this.config.copiedText;
      window.setTimeout(() => {
        hint.textContent = original;
      }, 2000);
    }

    /* ------------------------------------------------------------ birthday */

    async saveBirthday() {
      // Sin correo no hay a quién anotarle el cumpleaños: escribirlo de todos modos
      // se lo cuelga al perfil anónimo de Klaviyo, que no sirve para nada.
      if (!this.email) return;

      const day = Number(this.querySelector('[data-vs-day]').value);
      const month = Number(this.querySelector('[data-vs-month]').value);
      const error = this.querySelector('[data-vs-birthday-error]');

      if (!day || !month) {
        error.textContent = this.config.birthdayError;
        error.hidden = false;
        return;
      }

      error.hidden = true;

      /*
        Klaviyo needs a full date for the date-property flow to fire, but it only
        compares day and month. Hence the filler year in the schema: we do not ask
        for the real year so we do not collect data we never use.
      */
      const padded = (value) => String(value).padStart(2, '0');
      const birthdayDate = `${this.config.birthdayYear}-${padded(month)}-${padded(day)}`;

      // For the order it goes in readable form, with the configured month name.
      const selectMonth = this.querySelector('[data-vs-month]');
      const monthName = selectMonth.selectedOptions[0]?.textContent.trim() || String(month);
      const readable = `${monthName} ${day}`;

      const payload = {
        data: {
          type: 'profile',
          attributes: {
            email: this.email,
            properties: {
              birthday: birthdayDate,
              birthday_day: day,
              birthday_month: month
            }
          }
        }
      };

      try {
        await fetch(this.klaviyoUrl('/client/profiles'), {
          method: 'POST',
          headers: this.klaviyoHeaders(),
          body: JSON.stringify(payload)
        });
      } catch {
        /* The email is already captured: a failure here must not block the customer. */
      }

      /*
        Optional copy to the cart, so the birthday shows up on the order. It runs
        after Klaviyo and in its own try: Klaviyo is the real record, this is a
        convenience for the admin and must not be able to take it down.

        Deliberately limited reach: the attribute lives in THIS browser's cart, so
        it only reaches the order of someone who buys without switching device or
        clearing cookies.
      */
      if (this.config.orderAttribute) {
        try {
          const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
          await fetch(`${root}/cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { [this.config.orderAttribute]: readable } })
          });
        } catch {
          /* The birthday is already in Klaviyo; this is a bonus. */
        }
      }

      this.birthdayForm.hidden = true;

      const done = this.querySelector('[data-vs-birthday-done]');
      if (done) done.hidden = false;

      report('scratch_birthday', { month });
    }
  }

  if (!window.customElements.get('venner-scratch')) {
    window.customElements.define('venner-scratch', VennerScratch);
  }
})();
